import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Divider,
  GridProps
} from '@mui/material';
import DrillDownFinancialChips from './FinancialChips';
import DrillDownOperationalStats from './OperationalStats';
import DrillDownPeriodComparison from './PeriodComparison';
import { PeriodData, PeriodComparison } from './types';
import { DrillDownConfig } from './configs';

interface DrillDownPeriodSummaryProps {
  periodo?: PeriodData;
  comparativo_periodo?: PeriodComparison;
  transactionType: 'compras' | 'vendas';
  config: DrillDownConfig;
  title?: string;
  showFinancialChips?: boolean;
  showOperationalStats?: boolean;
  showPeriodComparison?: boolean;
  showDivider?: boolean;
  containerProps?: GridProps;
}

const DrillDownPeriodSummary: React.FC<DrillDownPeriodSummaryProps> = ({
  periodo,
  comparativo_periodo,
  transactionType,
  config,
  title,
  showFinancialChips = true,
  showOperationalStats = true,
  showPeriodComparison = true,
  showDivider = true,
  containerProps
}) => {
  // Validar se há dados do período para renderizar
  if (!periodo) {
    return null;
  }

  const defaultTitle = `📅 Análise Detalhada - ${periodo.periodo}`;

  // Função para obter o valor da transação baseado no tipo
  const getTransactionValue = () => {
    return transactionType === 'compras' ? periodo.total_compras : periodo.total_vendas;
  };

  // Função para obter o valor da maior transação
  const getMaiorTransacao = () => {
    return transactionType === 'compras' ? periodo.maior_compra : periodo.maior_venda;
  };

  // Função para obter o valor da menor transação
  const getMenorTransacao = () => {
    return transactionType === 'compras' ? periodo.menor_compra : periodo.menor_venda;
  };

  // Função para obter o total de parceiros
  const getTotalParceiros = () => {
    return transactionType === 'compras' ? periodo.total_fornecedores : periodo.total_clientes;
  };

  return (
    <Grid item xs={12} {...containerProps}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          {title || defaultTitle}
        </Typography>
        
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Primeira linha - Valores Principais */}
          {showFinancialChips && (
            <DrillDownFinancialChips
              chips={[
                {
                  label: config.periodSummaryLabels.totalValue,
                  value: getTransactionValue() || 0,
                  color: 'success'
                },
                {
                  label: config.periodSummaryLabels.ticketMedio,
                  value: periodo.ticket_medio || 0,
                  color: 'primary'
                },
                {
                  label: config.periodSummaryLabels.maiorTransacao,
                  value: getMaiorTransacao() || 0,
                  color: 'warning'
                },
                {
                  label: config.periodSummaryLabels.menorTransacao,
                  value: getMenorTransacao() || 0,
                  color: 'info'
                }
              ]}
            />
          )}

          {/* Segunda linha - Quantidades */}
          {showOperationalStats && (
            <DrillDownOperationalStats 
              data={{
                total_documentos: periodo.total_documentos,
                total_fornecedores: getTotalParceiros() || 0,
                total_produtos: periodo.total_produtos
              }}
              labels={{
                documentos: config.periodSummaryLabels.totalDocuments,
                fornecedores: config.periodSummaryLabels.totalPartners,
                produtos: config.periodSummaryLabels.totalProducts
              }}
            />
          )}

          {/* Terceira linha - Comparativos */}
          {showPeriodComparison && comparativo_periodo && (
            <DrillDownPeriodComparison 
              comparativo_periodo={comparativo_periodo}
              title="Análise Comparativa"
            />
          )}
        </Grid>
      </Box>
      {showDivider && <Divider sx={{ mb: 3 }} />}
    </Grid>
  );
};

export default DrillDownPeriodSummary; 